import * as React from "react";
import { useCallback, useImperativeHandle, useRef } from "react";

import { standaloneInputClassName } from "@/components/podkit/forms/input-classes";
import { cn } from "@/components/podkit/lib/cn";
import { useAutosizeTextArea } from "@/hooks/use-autosize-textarea";

export type TextareaProps = {
    maxHeight?: number;
    minHeight?: number;
    dontAutosize?: boolean;
    enterKeyHint?: "enter" | "done" | "go" | "next" | "previous" | "search" | "send";
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>;

// Monotonic counter avoids UUID generation and the extra render cycle that
// the previous useState+useEffect approach caused on every keystroke.
let autosizeSeq = 0;

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
    (
        {
            maxHeight = Number.MAX_SAFE_INTEGER,
            minHeight,
            className,
            value,
            dontAutosize,
            onKeyDown,
            onCompositionStart,
            onCompositionEnd,
            ...props
        }: TextareaProps,
        ref: React.Ref<HTMLTextAreaElement>,
    ) => {
        const textAreaRef = React.useRef<HTMLTextAreaElement | null>(null);
        useImperativeHandle(ref, () => textAreaRef.current!);

        const isComposingRef = useRef(false);

        // Derive the autosize trigger directly from value, avoiding an extra
        // state variable + useEffect that caused a redundant render per keystroke.
        const triggerAutoSize = typeof value === "string" ? value : String(++autosizeSeq);

        useAutosizeTextArea({
            textArea: textAreaRef.current,
            triggerAutoSize,
            maxHeight,
            minHeight,
            enabled: !dontAutosize,
        });

        const handleCompositionStart = useCallback(
            (e: React.CompositionEvent<HTMLTextAreaElement>) => {
                isComposingRef.current = true;
                onCompositionStart?.(e);
            },
            [onCompositionStart],
        );

        const handleCompositionEnd = useCallback(
            (e: React.CompositionEvent<HTMLTextAreaElement>) => {
                isComposingRef.current = false;
                onCompositionEnd?.(e);
            },
            [onCompositionEnd],
        );

        const handleKeyDown = useCallback(
            (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                // Don't handle key events during IME composition for Enter and Tab keys
                if (isComposingRef.current && (e.key === "Enter" || e.key === "Tab")) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }
                onKeyDown?.(e);
            },
            [onKeyDown],
        );

        return (
            <textarea
                {...props}
                value={value}
                ref={textAreaRef}
                onKeyDown={handleKeyDown}
                onCompositionStart={handleCompositionStart}
                onCompositionEnd={handleCompositionEnd}
                // We want an auto-sized textarea to have the same height as a one-line input field which is 36px.
                // However, our default line-height of 20px will cause scrollbars to appear because the available pixels
                // for the content-area would be 18 (36 - 2*8 - 2x1 = 18) which is less than 20.
                className={cn(standaloneInputClassName, "h-auto max-w-full overflow-y-auto leading-[18px]", className)}
            />
        );
    },
);

Textarea.displayName = "Textarea";
